import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  CalendarCheck,
  CalendarClock,
  Download,
  IndianRupee,
  Plus,
  RefreshCw,
  Wallet,
} from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { MetricTile } from '@/components/ui/MetricTile';
import { Pagination } from '@/components/ui/Pagination';
import { QueryError } from '@/components/ui/QueryError';
import { SkeletonLines } from '@/components/ui/Skeleton';
import {
  PAYMENT_STATUS_LABEL,
  PAYMENT_STATUS_TONE,
  SUBSCRIPTION_PAYMENT_METHOD_LABEL,
} from '@/constants/institute';
import { ROLES } from '@/constants/roles';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import { formatCurrency, formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { SubscriptionPayment, SubscriptionReceipts } from '@/types/institute';
import { DeckPageHeader } from '../../components/DeckPageHeader';
import { institutesApi } from '../api/institutes.api';
import { RecordSubscriptionPaymentModal } from '../components/RecordSubscriptionPaymentModal';
import { SubscriptionReceiptsPrint } from '../components/SubscriptionReceiptDocument';
import { isSubscriptionOverdue } from '../utils';

const PAGE_SIZE = 20;

/** The ledger's page, as it sits in the address bar. */
type Filters = {
  page: number;
};

const DEFAULT_FILTERS: Filters = { page: 1 };

/**
 * One customer's licence ledger: every payment, newest first, each with the
 * day it was paid and the day the licence it bought comes up for renewal.
 *
 * The latest payment leads the page in its own panel because it answers the
 * question support is asked most — "when did they last pay, and until when
 * are they covered?" — without reading a list. The next renewal beside it is
 * the institute's due date, which recording a payment moves forward.
 */
export function InstituteSubscriptionPage() {
  const { uuid = '' } = useParams();
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS);
  const { page } = filters;
  const [isRecordOpen, setIsRecordOpen] = useState(false);
  /** What is on its way to the print dialog. */
  const [receipts, setReceipts] = useState<SubscriptionReceipts | null>(null);
  /** Which download is being fetched: a payment's uuid, `all`, or nothing. */
  const [fetching, setFetching] = useState<string | null>(null);
  const role = useAuthStore((state) => state.user?.role);
  // Support reads the ledger; only a SUPER_ADMIN enters money against it.
  const canRecord = role === ROLES.SUPER_ADMIN;

  const institute = useApiQuery(() => institutesApi.get(uuid), [uuid]);
  const ledger = useApiQuery(
    () => institutesApi.subscriptionPayments(uuid, { page, limit: PAGE_SIZE }),
    [uuid, page],
  );

  const backTo = { to: `/platform/institutes/${uuid}`, label: 'Back to institute' };

  /**
   * Fetched from the server at the moment of download rather than printed from
   * what is on screen: the "all" bundle needs every page of the ledger, and the
   * server records each download in the audit log (CLAUDE.md §9).
   */
  async function download(paymentUuid: string | 'all') {
    setFetching(paymentUuid);
    try {
      setReceipts(
        paymentUuid === 'all'
          ? await institutesApi.subscriptionReceipts(uuid)
          : await institutesApi.subscriptionReceipt(uuid, paymentUuid),
      );
    } catch (caught) {
      toast.error(
        'Receipt not downloaded',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setFetching(null);
    }
  }

  if (institute.error) {
    return (
      <QueryError
        title="Could not load this institute"
        error={institute.error}
        onRetry={institute.refetch}
      />
    );
  }

  if (!institute.data) {
    return (
      <>
        <DeckPageHeader
          eyebrow="Subscription"
          title="Loading payments…"
          description="Fetching this customer's licence ledger."
          backTo={backTo}
        />
        <div className="card p-5">
          <SkeletonLines count={6} />
        </div>
      </>
    );
  }

  const record = institute.data;
  const summary = ledger.data?.summary;
  const latest = summary?.lastPayment ?? null;
  const isOverdue = isSubscriptionOverdue(record.subscriptionDueDate, record.paymentStatus);

  const recordButton = (size: 'sm' | 'md') =>
    canRecord && (
      <Button size={size} leftIcon={<Plus className="h-4 w-4" />} onClick={() => setIsRecordOpen(true)}>
        Record payment
      </Button>
    );

  return (
    <>
      <DeckPageHeader
        eyebrow="Subscription"
        title={record.displayName}
        description="Licence payments and renewals, most recent first."
        backTo={backTo}
        actions={
          <>
            <Button
              variant="secondary"
              leftIcon={<Download className="h-4 w-4" />}
              isLoading={fetching === 'all'}
              disabled={!summary || summary.paymentCount === 0 || fetching !== null}
              onClick={() => void download('all')}
            >
              Download all receipts
            </Button>
            {recordButton('md')}
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={PAYMENT_STATUS_TONE[record.paymentStatus]} withDot>
            {PAYMENT_STATUS_LABEL[record.paymentStatus]}
          </Badge>
          <span className="rounded-pill border border-line bg-surface px-3 py-1 text-2xs text-ink-2">
            Agreed fee{' '}
            <span className="font-[650] tabular-nums text-ink">
              {formatCurrency(record.subscriptionAmount)}
            </span>
          </span>
        </div>
      </DeckPageHeader>

      {ledger.error ? (
        <QueryError title="Could not load payments" error={ledger.error} onRetry={ledger.refetch} />
      ) : (
        <>
          {/* ---------------- where they stand ---------------- */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <LatestPaymentPanel
              payment={latest}
              isLoading={!summary}
              isDownloading={latest !== null && fetching === latest.uuid}
              onDownload={() => latest && void download(latest.uuid)}
            />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
              <MetricTile
                label="Next renewal"
                icon={<RefreshCw className="h-4 w-4" />}
                tone={isOverdue ? 'rose' : 'indigo'}
                value={record.subscriptionDueDate ? formatDate(record.subscriptionDueDate) : '—'}
                hint={
                  record.subscriptionDueDate
                    ? isOverdue
                      ? 'Overdue — renewal not yet paid'
                      : 'The institute’s current due date'
                    : 'No renewal date set'
                }
              />
              <MetricTile
                label="Total received"
                icon={<Wallet className="h-4 w-4" />}
                tone="teal"
                isLoading={!summary}
                value={summary ? formatCurrency(summary.totalPaid) : '—'}
                hint={
                  summary &&
                  `${summary.paymentCount} payment${summary.paymentCount === 1 ? '' : 's'} across ${summary.byYear.length} year${summary.byYear.length === 1 ? '' : 's'}`
                }
              />
            </div>
          </div>

          {/* ---------------- year by year ---------------- */}
          {summary && summary.byYear.length > 0 && (
            <section className="card p-4" aria-labelledby="by-year-heading">
              <h2 id="by-year-heading" className="lbl mb-3">
                Paid by year
              </h2>
              <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                {summary.byYear.map((year) => (
                  <li
                    key={year.periodYear}
                    className="rounded-field border border-line bg-surface-2 px-3 py-2"
                  >
                    <p className="text-xs text-ink-3">{year.periodYear}</p>
                    <p className="text-sm font-[650] tabular-nums text-ink">
                      {formatCurrency(year.totalPaid)}
                    </p>
                    <p className="text-2xs text-ink-3">
                      {year.paymentCount} payment{year.paymentCount === 1 ? '' : 's'}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ---------------- every transaction ---------------- */}
          <section className="card overflow-hidden" aria-labelledby="history-heading">
            <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
              <h2 id="history-heading" className="lbl">
                Transaction history
              </h2>
              {ledger.data && ledger.data.meta.total > 0 && (
                <span className="text-xs text-ink-3">Most recent first</span>
              )}
            </header>

            {ledger.isLoading && !ledger.data ? (
              <div className="p-4">
                <SkeletonLines count={5} />
              </div>
            ) : !ledger.data || ledger.data.items.length === 0 ? (
              <EmptyState
                icon={<IndianRupee className="h-5 w-5" />}
                title="No payments recorded yet"
                description={
                  canRecord
                    ? 'When this institute pays for its licence, record it here with its renewal date.'
                    : 'Payments appear here once a super admin records them.'
                }
                action={recordButton('sm')}
              />
            ) : (
              <ol className="divide-y divide-line" aria-busy={ledger.isLoading}>
                {ledger.data.items.map((payment, index) => (
                  <TransactionRow
                    key={payment.uuid}
                    payment={payment}
                    // Newest first, so the very first row of page one is the latest.
                    isLatest={page === 1 && index === 0}
                    isDownloading={fetching === payment.uuid}
                    onDownload={() => void download(payment.uuid)}
                  />
                ))}
              </ol>
            )}

            {ledger.data && (
              <Pagination
                meta={ledger.data.meta}
                onPageChange={(next) => setFilters({ page: next })}
                itemLabel="payments"
              />
            )}
          </section>
        </>
      )}

      <SubscriptionReceiptsPrint target={receipts} onFinished={() => setReceipts(null)} />

      {canRecord && (
        <RecordSubscriptionPaymentModal
          isOpen={isRecordOpen}
          onClose={() => setIsRecordOpen(false)}
          institute={record}
          onRecorded={(payment) => {
            setIsRecordOpen(false);
            toast.success(
              'Payment recorded',
              `${formatCurrency(payment.amount)} for ${payment.periodYear}, renewing ${formatDate(payment.renewsOn)}.`,
            );
            // The due date may have moved, and the newest payment is on page one.
            institute.refetch();
            if (page === 1) ledger.refetch();
            else setFilters({ page: 1 });
          }}
        />
      )}
    </>
  );
}

// --- pieces -----------------------------------------------------------------

/** The most recent payment, set out in full. */
function LatestPaymentPanel({
  payment,
  isLoading,
  isDownloading,
  onDownload,
}: {
  payment: SubscriptionPayment | null;
  isLoading: boolean;
  isDownloading: boolean;
  onDownload: () => void;
}) {
  return (
    <section
      className="card flex flex-col gap-4 border-accent-line p-5 lg:col-span-2"
      aria-labelledby="latest-heading"
    >
      <header className="flex items-center justify-between gap-2">
        <h2 id="latest-heading" className="lbl">
          Latest payment
        </h2>
        {payment && (
          <span className="pill bg-accent-soft text-accent">For {payment.periodYear}</span>
        )}
      </header>

      {isLoading ? (
        <SkeletonLines count={3} />
      ) : !payment ? (
        <p className="text-sm text-ink-3">Nothing has been received yet.</p>
      ) : (
        <>
          <p className="text-2xl font-[750] tabular-nums tracking-tighter text-ink">
            {formatCurrency(payment.amount)}
          </p>
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DateFact
              icon={<CalendarCheck className="h-4 w-4" />}
              label="Paid on"
              value={formatDate(payment.paidOn)}
            />
            <DateFact
              icon={<CalendarClock className="h-4 w-4" />}
              label="Renews on"
              value={formatDate(payment.renewsOn)}
            />
          </dl>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-ink-3">
              {payment.receiptNumber} · {SUBSCRIPTION_PAYMENT_METHOD_LABEL[payment.method]}
              {payment.referenceNo && <> · Ref {payment.referenceNo}</>}
              {payment.recordedBy && <> · Recorded by {payment.recordedBy}</>}
            </p>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Download className="h-3.5 w-3.5" />}
              isLoading={isDownloading}
              onClick={onDownload}
            >
              Download receipt
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

/** A date with its own label and icon, so "paid" and "renews" never read alike. */
function DateFact({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 rounded-field border border-line bg-surface-2 px-3 py-2.5">
      <span
        className="grid h-8 w-8 shrink-0 place-items-center rounded-field bg-accent-soft text-accent"
        aria-hidden
      >
        {icon}
      </span>
      <div className="min-w-0">
        <dt className="text-xs text-ink-3">{label}</dt>
        <dd className="text-sm font-[650] text-ink">{value}</dd>
      </div>
    </div>
  );
}

/**
 * One transaction on the timeline: when it was paid on the left, what it was in
 * the middle, when it renews on the right — the three things read from it, in
 * the order they are asked.
 */
function TransactionRow({
  payment,
  isLatest,
  isDownloading,
  onDownload,
}: {
  payment: SubscriptionPayment;
  isLatest: boolean;
  isDownloading: boolean;
  onDownload: () => void;
}) {
  const paid = new Date(`${payment.paidOn}T00:00:00`);

  return (
    <li className={cn('flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center', isLatest && 'bg-surface-2')}>
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {/* A calendar leaf, so the payment day is the first thing the eye lands on. */}
        <div
          className="grid w-12 shrink-0 place-items-center rounded-field border border-line bg-surface py-1 text-center"
          aria-hidden
        >
          <span className="text-2xs font-[650] uppercase text-accent">
            {paid.toLocaleDateString('en-IN', { month: 'short' })}
          </span>
          <span className="text-base font-[750] leading-none tabular-nums text-ink">
            {paid.getDate()}
          </span>
          <span className="text-2xs tabular-nums text-ink-3">{paid.getFullYear()}</span>
        </div>

        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-[650] tabular-nums text-ink">
              {formatCurrency(payment.amount)}
            </span>
            {isLatest && (
              <Badge tone="info" withDot>
                Latest
              </Badge>
            )}
          </p>
          <p className="truncate text-xs text-ink-2">
            Paid on {formatDate(payment.paidOn)} · for {payment.periodYear} ·{' '}
            {SUBSCRIPTION_PAYMENT_METHOD_LABEL[payment.method]}
            {payment.referenceNo && <> · {payment.referenceNo}</>}
          </p>
          {payment.note && <p className="mt-0.5 truncate text-xs text-ink-3">{payment.note}</p>}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2 sm:ml-auto">
        <span className="inline-flex w-fit items-center gap-1.5 rounded-pill border border-line bg-surface px-3 py-1 text-xs text-ink-2">
          <RefreshCw className="h-3 w-3 text-accent" aria-hidden />
          Renews on <span className="font-[650] text-ink">{formatDate(payment.renewsOn)}</span>
        </span>
        <Button
          variant="secondary"
          size="sm"
          iconOnly
          aria-label={`Download receipt ${payment.receiptNumber}`}
          title={`Download receipt ${payment.receiptNumber}`}
          isLoading={isDownloading}
          onClick={onDownload}
        >
          {!isDownloading && <Download className="h-3.5 w-3.5" aria-hidden />}
        </Button>
      </div>
    </li>
  );
}
